// ── Early Bird 列表数据源 — earlybird_projects 表读取 ─────────────────
// 供早期雷达 Early Bird 子 tab 使用：
// - fetchEarlyBirdCounts: 导航 chip 的分类计数（web3 池按 tax_system/tax_category 聚合）
// - fetchEarlyBirdProjects: 按分类/体系分页拉列表
// 表由 scripts/ingest-earlybird.mjs 灌入（见 migrations/014），RLS 匿名只读。
import { supabase } from '../lib/supabase';

const LIST_COLUMNS =
  'id,project_name,one_liner,description,organization,program,award_date,source_url,logo_url,' +
  'tax_system,tax_category,tax_confidence,' +
  'github_url,github_stars,github_pushed_at,github_top_contributors,github_checked_at,' +
  'project_live_url,site_alive,site_checked_at,twitter_handle,' +
  'ai_narrative,ai_narrative_en,ai_business,ai_business_en,ai_tech_zh,updated_at,momentum_score,momentum_computed_at';

// 排序维度 → 底层列 + 方向。award_date 是默认（最新收录优先）；github_stars/recent_push/
// momentum 依赖 enrichment/快照数据，没有该数据的行统一排在后面（nullsFirst: false）。
const SORT_COLUMNS = {
  award_date: 'award_date',
  github_stars: 'github_stars',
  recent_push: 'github_pushed_at',
  momentum: 'momentum_score',
};

/**
 * 导航 chip 计数 — 读 earlybird_category_counts 视图（数据库端 group by，~18 行）。
 * 不能在客户端拉全表聚合：PostgREST 单次请求默认上限 1000 行，1,829 行的
 * web3 池会被静默截断（真实踩过：总数显示 1,000）。
 * supabase 未配置或查询失败返回 null，调用方按「计数不可用」处理（隐藏 chip 行，不显示 0）。
 */
export async function fetchEarlyBirdCounts() {
  if (!supabase) return null;

  const { data, error } = await supabase.from('earlybird_category_counts').select('*');
  if (error || !data) return null;

  const product = {};
  const ecosystem = {};
  let others = 0;
  let web3Total = 0;
  let nonWeb3Total = 0;
  for (const row of data) {
    if (!row.is_web3) { nonWeb3Total += row.cnt; continue; }
    web3Total += row.cnt;
    if (row.tax_system === 'product') product[row.tax_category] = (product[row.tax_category] || 0) + row.cnt;
    else if (row.tax_system === 'ecosystem') ecosystem[row.tax_category] = (ecosystem[row.tax_category] || 0) + row.cnt;
    else others += row.cnt;
  }
  return { product, ecosystem, others, web3Total, nonWeb3Total };
}

/**
 * 具体活动场次下拉选项 — 读 earlybird_program_counts 视图（数据库端 group by）。
 * 同 fetchEarlyBirdCounts 一样避免客户端聚合触发 PostgREST 1000 行截断。
 * @param {boolean} isWeb3
 * @returns {Promise<Array<{program: string, cnt: number}>>} 按数量降序，失败返回 []
 */
export async function fetchEarlyBirdPrograms(isWeb3 = true) {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from('earlybird_program_counts')
    .select('program, cnt')
    .eq('is_web3', isWeb3)
    .order('cnt', { ascending: false });
  if (error || !data) return [];
  return data;
}

/**
 * 分页拉项目列表。
 * @param {object} opts
 * @param {boolean} [opts.isWeb3=true]
 * @param {string|null} [opts.category] - 精确分类（chip 选中态）；与 system 互斥，category 优先
 * @param {string|null} [opts.system] - 'product' | 'ecosystem'（整个体系筛选）
 * @param {string|null} [opts.organization] - 精确来源机构筛选（web3 池：'ETHGlobal Showcase' | 'Ethereum Foundation ESP' | 'Colosseum'）
 * @param {boolean} [opts.hasGithub=false] - 只看解析到公开仓库的项目
 * @param {'alive'|'dead'|'unchecked'|null} [opts.siteAlive=null] - 项目网站存活状态
 *   （'alive'=site_alive true, 'dead'=site_alive false, 'unchecked'=site_checked_at 为空，即未探测）
 * @param {boolean} [opts.hasProfile=false] - 只看已生成 AI 叙事画像的项目
 * @param {'high'|null} [opts.taxConfidence=null] - 只看高置信度分类
 * @param {string|null} [opts.program=null] - 精确活动场次筛选
 * @param {'award_date'|'github_stars'|'recent_push'} [opts.sortBy='award_date']
 * @param {number} [opts.page=0]
 * @param {number} [opts.pageSize=20]
 * @returns {Promise<{items: object[], total: number}>} 失败时 items 空、total 0
 */
export async function fetchEarlyBirdProjects({
  isWeb3 = true,
  category = null,
  system = null,
  organization = null,
  hasGithub = false,
  siteAlive = null,
  hasProfile = false,
  taxConfidence = null,
  program = null,
  sortBy = 'award_date',
  page = 0,
  pageSize = 20,
} = {}) {
  if (!supabase) return { items: [], total: 0 };

  let query = supabase
    .from('earlybird_projects')
    .select(LIST_COLUMNS, { count: 'exact' })
    .eq('is_web3', isWeb3);

  if (category) query = query.eq('tax_category', category);
  else if (system) query = query.eq('tax_system', system);

  if (organization) query = query.eq('organization', organization);
  if (hasGithub) query = query.not('github_url', 'is', null);
  if (siteAlive === 'alive') query = query.eq('site_alive', true);
  else if (siteAlive === 'dead') query = query.eq('site_alive', false);
  else if (siteAlive === 'unchecked') query = query.is('site_checked_at', null);
  if (hasProfile) query = query.not('ai_narrative', 'is', null);
  if (taxConfidence) query = query.eq('tax_confidence', taxConfidence);
  if (program) query = query.eq('program', program);

  const from = page * pageSize;
  const { data, count, error } = await query
    .order(SORT_COLUMNS[sortBy] || SORT_COLUMNS.award_date, { ascending: false, nullsFirst: false })
    .range(from, from + pageSize - 1);

  if (error) return { items: [], total: 0 };
  return { items: data || [], total: count ?? 0 };
}

/**
 * 按 id 精确取单条项目——支撑 wiki 弹窗的可分享深链（#earlybird/:id）：
 * 从 URL 恢复时不知道这条项目现在应该出现在分页/筛选后的第几页，直接按 id 查最简单。
 * @param {string} id
 * @returns {Promise<object|null>}
 */
export async function fetchEarlyBirdProjectById(id) {
  if (!supabase || !id) return null;
  const { data, error } = await supabase
    .from('earlybird_projects')
    .select(LIST_COLUMNS)
    .eq('id', id)
    .maybeSingle();
  if (error) return null;
  return data || null;
}

/**
 * 批量取当前页项目的 wiki 缓存摘要（founders/company 低置信度预览 + founderPedigreeHits
 * 结构化人才背景信号，卡片上直接用）。只读缓存，不触发生成——卡片刷出来就顺带跑一次
 * AI 综合，2000+ 项目滚动一遍等于批量触发付费生成，成本完全失控。只有已经点过
 * "查看档案"/被批量预生成脚本覆盖过的项目才会有缓存，其余项目卡片上就是没有这个
 * 预览块，不做占位。
 * @param {string[]} ids
 * @returns {Promise<Record<string, {founders: string|null, company: string|null, founderPedigreeHits: Array<{name, type, evidence}>}>>}
 */
export async function fetchWikiBriefsForIds(ids) {
  if (!supabase || !ids?.length) return {};
  const { data, error } = await supabase
    .from('earlybird_wiki_cache')
    .select('project_id, wiki_report')
    .in('project_id', ids);
  if (error || !Array.isArray(data)) return {};
  return Object.fromEntries(
    data.map((row) => [row.project_id, {
      founders: row.wiki_report?.founders || null,
      foundersEn: row.wiki_report?.founders_en || null,
      company: row.wiki_report?.company || null,
      companyEn: row.wiki_report?.company_en || null,
      founderPedigreeHits: row.wiki_report?.founderPedigreeHits || [],
    }])
  );
}

// GitHub 活跃信号的新鲜度窗口：14 天内有 push 才算"活跃"。
const DIGEST_GITHUB_ACTIVE_DAYS = 14;

/**
 * 周报草稿的选品候选池——按"最近收录"取一批，标出有真实强信号的（Demo 存活 /
 * GitHub 最近有更新），有信号的排前面。具体挑哪几条收进草稿由调用方（UI）决定，
 * 这里只负责准备候选池 + 打信号标签。
 *
 * 注意：不用"双源零融资"这类标签——那是 RootData 热榜那边用 fundraise-check
 * 跨源核对出来的信号，Early Bird 项目池目前没有算过这个字段，硬套上去就是编的。
 * @param {object} [opts]
 * @param {number} [opts.days=7] - 收录窗口（按 created_at，即入库时间，不是
 *   award_date——award_date 跨来源含义不一致，见 scrape-colosseum.mjs 注释）
 * @param {number} [opts.limit=30] - 候选池上限
 * @returns {Promise<Array<object & {signal: 'site_alive'|'github_active'|null}>>}
 */
export async function fetchWeeklyDigestCandidates({ days = 7, limit = 30 } = {}) {
  if (!supabase) return [];
  const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
  const { data, error } = await supabase
    .from('earlybird_projects')
    .select(LIST_COLUMNS)
    .eq('is_web3', true)
    .gte('created_at', since)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error || !Array.isArray(data)) return [];

  const activeCutoff = Date.now() - DIGEST_GITHUB_ACTIVE_DAYS * 24 * 60 * 60 * 1000;
  return data
    .map((p) => {
      const githubActive = !!p.github_pushed_at && new Date(p.github_pushed_at).getTime() >= activeCutoff;
      const signal = p.site_alive ? 'site_alive' : githubActive ? 'github_active' : null;
      return { ...p, signal };
    })
    .sort((a, b) => (a.signal ? 0 : 1) - (b.signal ? 0 : 1));
}
